import React, { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    TextField,
    IconButton,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { Alert } from "@mui/material";
import {
    Error as ErrorIcon,
    Warning as WarningIcon,
    ExpandMore as ExpandMoreIcon,
    AccountTree as AccountTreeIcon,
    Description as DescriptionIcon,
    Publish as PublishIcon,
    Edit as EditIcon,
    Done as DoneIcon,
} from "@mui/icons-material";
import { VCard } from "../../../../core/components/VCard";
import { PrimaryButton, SecondaryButton } from "../../../layout/Button/Button";
import {
    ValidationResult,
    createFromWizard,
    ParsedStoryStructure,
    ParsedStoryPart,
} from "../../../../client/api/branchedStory";
import { useAlert } from "../../../../contexts/alert.context";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3), // 24px
    },
    summaryCard: {
        padding: theme.spacing(3), // 24px
    },
    storyInfo: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(2), // 16px
        marginBottom: theme.spacing(3), // 24px
    },
    infoItem: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5), // 4px
    },
    infoLabel: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
        fontWeight: 600,
    },
    infoValue: {
        fontWeight: 500,
    },
    validationSection: {
        marginTop: theme.spacing(2), // 16px
    },
    validationItem: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    themeChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5), // 4px
    },
    structurePreview: {
        maxHeight: 300,
        overflow: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2), // 16px
        backgroundColor: theme.palette.grey[50],
    },
    actions: {
        display: "flex",
        gap: theme.spacing(2), // 16px
        justifyContent: "flex-end",
        marginTop: theme.spacing(3), // 24px
    },
    publishSettings: {
        marginTop: theme.spacing(2), // 16px
        padding: theme.spacing(2), // 16px
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
    accordionSummaryContent: {
        display: "flex",
        alignItems: "center",
        width: "100%",
    },
    editField: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    questionEditBlock: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
    decisionPointEditBlock: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
    branchEditRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

interface ReviewAndPublishProps {
    parsedStory?: ParsedStoryStructure;
    metadata?: {
        storyName: string;
        author: string;
        synopsis?: string;
        themes?: number[];
        ageRange?: string;
        colour?: string;
        resources?: string;
        resourcesROI?: string;
    };
    validation?: ValidationResult;
    onComplete?: () => void;
    onParsedStoryChange?: (updated: ParsedStoryStructure) => void;
}

export const ReviewAndPublish: React.FC<ReviewAndPublishProps> = ({
    parsedStory,
    metadata,
    validation,
    onComplete,
    onParsedStoryChange,
}) => {
    const classes = useStyles();
    const { setAlert } = useAlert();
    const [loading, setLoading] = useState(false);

    // Track which parts are in edit mode, keyed by part index
    const [editingParts, setEditingParts] = useState<Record<number, boolean>>(() => {
        // Auto-open edit mode for low-confidence sections
        const initial: Record<number, boolean> = {};
        if (parsedStory?.parts) {
            parsedStory.parts.forEach((part, index) => {
                const conf = part.parseConfidence ?? 1.0;
                if (conf < 0.5) {
                    initial[index] = true;
                }
            });
        }
        return initial;
    });

    const toggleEditMode = (partIndex: number) => {
        setEditingParts((prev) => ({
            ...prev,
            [partIndex]: !prev[partIndex],
        }));
    };

    // --- Handlers for inline editing ---

    const handleContentChange = (partIndex: number, newContent: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        updatedParts[partIndex] = { ...updatedParts[partIndex], content: newContent };
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleQuestionTextChange = (partIndex: number, qIndex: number, newText: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const part = { ...updatedParts[partIndex] };
        const questions = [...(part.questions || [])];
        questions[qIndex] = { ...questions[qIndex], question: newText };
        part.questions = questions;
        updatedParts[partIndex] = part;
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleQuestionPromptChange = (partIndex: number, qIndex: number, newPrompt: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const part = { ...updatedParts[partIndex] };
        const questions = [...(part.questions || [])];
        questions[qIndex] = { ...questions[qIndex], prompt: newPrompt };
        part.questions = questions;
        updatedParts[partIndex] = part;
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleQuestionKeywordsChange = (partIndex: number, qIndex: number, newKeywords: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const part = { ...updatedParts[partIndex] };
        const questions = [...(part.questions || [])];
        questions[qIndex] = { ...questions[qIndex], keywords: newKeywords };
        part.questions = questions;
        updatedParts[partIndex] = part;
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleQuestionHint1Change = (partIndex: number, qIndex: number, newHint1: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const part = { ...updatedParts[partIndex] };
        const questions = [...(part.questions || [])];
        questions[qIndex] = { ...questions[qIndex], hint1: newHint1 };
        part.questions = questions;
        updatedParts[partIndex] = part;
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleDecisionTextChange = (partIndex: number, dpIndex: number, newText: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const updatedDPs = [...(updatedParts[partIndex].decisionPoints || [])];
        updatedDPs[dpIndex] = { ...updatedDPs[dpIndex], decisionText: newText };
        updatedParts[partIndex] = { ...updatedParts[partIndex], decisionPoints: updatedDPs };
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleBranchChoiceTextChange = (partIndex: number, dpIndex: number, branchIndex: number, newText: string) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const updatedDPs = [...(updatedParts[partIndex].decisionPoints || [])];
        const updatedBranches = [...updatedDPs[dpIndex].branches];
        updatedBranches[branchIndex] = { ...updatedBranches[branchIndex], choiceText: newText };
        updatedDPs[dpIndex] = { ...updatedDPs[dpIndex], branches: updatedBranches };
        updatedParts[partIndex] = { ...updatedParts[partIndex], decisionPoints: updatedDPs };
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    const handleBranchTargetChange = (partIndex: number, dpIndex: number, branchIndex: number, newTarget: number) => {
        if (!parsedStory || !onParsedStoryChange) return;
        const updatedParts = [...parsedStory.parts];
        const updatedDPs = [...(updatedParts[partIndex].decisionPoints || [])];
        const updatedBranches = [...updatedDPs[dpIndex].branches];
        updatedBranches[branchIndex] = { ...updatedBranches[branchIndex], targetPartNumber: newTarget };
        updatedDPs[dpIndex] = { ...updatedDPs[dpIndex], branches: updatedBranches };
        updatedParts[partIndex] = { ...updatedParts[partIndex], decisionPoints: updatedDPs };
        onParsedStoryChange({ ...parsedStory, parts: updatedParts });
    };

    // --- Confidence chip helper ---

    const renderConfidenceChip = (part: ParsedStoryPart) => {
        const conf = part.parseConfidence ?? 1.0;
        const label = conf >= 0.8 ? "Parsed" : conf >= 0.3 ? "Review needed" : "Manual entry needed";
        const color: "success" | "warning" | "error" = conf >= 0.8 ? "success" : conf >= 0.3 ? "warning" : "error";

        return <Chip label={label} color={color} size="small" sx={{ ml: 1 }} />;
    };

    const handleSaveAsDraft = async () => {
        if (!parsedStory) {
            setAlert("Error: No story data available. Please go back and upload a story file.", "error");
            return;
        }

        setLoading(true);
        try {
            // Merge edited metadata back into parsedStory
            const mergedStory = {
                ...parsedStory,
                title: metadata?.storyName || parsedStory?.title || "",
                author: metadata?.author || parsedStory?.author || "",
                synopsis: metadata?.synopsis || parsedStory?.synopsis,
                ageRange: metadata?.ageRange || parsedStory?.ageRange,
                colour: metadata?.colour || parsedStory?.colour,
                resources: metadata?.resources || parsedStory?.resources,
                resourcesROI: metadata?.resourcesROI || parsedStory?.resourcesROI,
            };

            console.log("Merged story for saving:", mergedStory);

            // Call API to save story as draft (include theme IDs from metadata step)
            const response = await createFromWizard(mergedStory, false, metadata?.themes);

            if (!response.success) {
                setAlert(`Failed to save: ${response.error || "Unknown error"}`, "error");
                setLoading(false);
                return;
            }

            console.log("Story saved as draft with ID:", response.storyId);
            setAlert(`Story "${mergedStory.title}" saved as draft.`, "success");

            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error("Error saving story as draft:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            setAlert(`Failed to save story as draft: ${errorMessage}`, "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        console.log("=== ReviewAndPublish: Publish ===");
        console.log("parsedStory:", parsedStory);
        console.log("metadata:", metadata);

        if (!parsedStory) {
            setAlert("Error: No story data available. Please go back and upload a story file.", "error");
            return;
        }

        setLoading(true);
        try {
            // Merge edited metadata back into parsedStory
            const mergedStory = {
                ...parsedStory,
                title: metadata?.storyName || parsedStory?.title || "",
                author: metadata?.author || parsedStory?.author || "",
                synopsis: metadata?.synopsis || parsedStory?.synopsis,
                ageRange: metadata?.ageRange || parsedStory?.ageRange,
                colour: metadata?.colour || parsedStory?.colour,
                resources: metadata?.resources || parsedStory?.resources,
                resourcesROI: metadata?.resourcesROI || parsedStory?.resourcesROI,
            };

            console.log("Merged story for publishing:", mergedStory);

            // Call API to publish story (include theme IDs from metadata step)
            const response = await createFromWizard(mergedStory, true, metadata?.themes);

            if (!response.success) {
                setAlert(`Failed to publish: ${response.error || "Unknown error"}`, "error");
                setLoading(false);
                return;
            }

            console.log("Story published with ID:", response.storyId);
            setAlert(`Story "${mergedStory.title}" has been published and is now available for sessions.`, "success");

            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error("Error publishing story:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            setAlert(`Failed to publish story: ${errorMessage}`, "error");
        } finally {
            setLoading(false);
        }
    };

    const renderStoryInfo = () => {
        const displayTitle = metadata?.storyName || parsedStory?.title || "Untitled Story";
        const displayAuthor = metadata?.author || parsedStory?.author || "Unknown Author";
        const displaySynopsis = metadata?.synopsis || parsedStory?.synopsis || "No synopsis provided";
        const displayAgeRange = metadata?.ageRange || parsedStory?.ageRange || "Not specified";
        const displayColour = metadata?.colour || parsedStory?.colour || "Not specified";

        return (
            <div className={classes.storyInfo}>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Title</div>
                    <div className={classes.infoValue}>{displayTitle}</div>
                </div>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Author</div>
                    <div className={classes.infoValue}>{displayAuthor}</div>
                </div>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Synopsis</div>
                    <div className={classes.infoValue}>{displaySynopsis}</div>
                </div>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Age Range</div>
                    <div className={classes.infoValue}>{displayAgeRange}</div>
                </div>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Card Color</div>
                    <div className={classes.infoValue}>{displayColour}</div>
                </div>
                <div className={classes.infoItem}>
                    <div className={classes.infoLabel}>Story Parts</div>
                    <div className={classes.infoValue}>{parsedStory?.parts?.length || 0} parts</div>
                </div>
            </div>
        );
    };

    const renderValidationResults = () => {
        if (!validation) {
            return (
                <Alert severity="info">Story validation was performed during upload. Check the summary below.</Alert>
            );
        }

        const { valid, errors, warnings, stats } = validation;

        return (
            <div className={classes.validationSection}>
                {valid ? (
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        Story structure is valid and ready for publication.
                    </Alert>
                ) : (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        Story has validation errors that must be fixed before publishing.
                    </Alert>
                )}

                {/* Story Statistics */}
                <Typography variant="subtitle2" gutterBottom>
                    Story Structure Summary
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    {stats.totalParts} parts &bull; {stats.totalDecisionPoints} decision points &bull;{" "}
                    {stats.totalBranches} branches &bull; {stats.maxDepth} max depth &bull; {stats.endPoints} end points
                </Typography>

                {/* Errors */}
                {errors.length > 0 && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography color="error">
                                {errors.length} Error{errors.length > 1 ? "s" : ""} Found
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense style={{ width: "100%" }}>
                                {errors.map((error, index) => (
                                    <ListItem key={index} className={classes.validationItem}>
                                        <ListItemIcon>
                                            <ErrorIcon color="error" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={error.message}
                                            primaryTypographyProps={{ variant: "body2" }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Warnings */}
                {warnings.length > 0 && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ color: "#ff9800" }}>
                                {warnings.length} Warning{warnings.length > 1 ? "s" : ""} Found
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense style={{ width: "100%" }}>
                                {warnings.map((warning, index) => (
                                    <ListItem key={index} className={classes.validationItem}>
                                        <ListItemIcon>
                                            <WarningIcon style={{ color: "#ff9800" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={warning.message}
                                            primaryTypographyProps={{ variant: "body2" }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )}
            </div>
        );
    };

    const renderQuestionReadOnly = (
        question: ParsedStoryPart["questions"] extends (infer Q)[] | undefined ? Q : never,
        qIndex: number,
    ) => (
        <ListItem key={qIndex} style={{ flexDirection: "column", alignItems: "flex-start" }}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
                {qIndex + 1}. {question.question}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                Category: {question.category}
                {question.keywords ? ` \u2022 Keywords: ${question.keywords}` : ""}
            </Typography>
            {question.prompt ? (
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                    Prompts: {question.prompt}
                </Typography>
            ) : null}
            {question.hint1 ? (
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                    Help question: {question.hint1}
                </Typography>
            ) : null}
        </ListItem>
    );

    const renderQuestionEditable = (partIndex: number, qIndex: number) => {
        const part = parsedStory?.parts[partIndex];
        if (!part?.questions) return null;
        const question = part.questions[qIndex];

        return (
            <Box key={qIndex} className={classes.questionEditBlock}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Question {qIndex + 1} ({question.category})
                </Typography>
                <TextField
                    label="Question text"
                    value={question.question}
                    onChange={(e) => handleQuestionTextChange(partIndex, qIndex, e.target.value)}
                    fullWidth
                    size="small"
                    className={classes.editField}
                />
                <TextField
                    label="Prompts (one per line)"
                    value={question.prompt || ""}
                    onChange={(e) => handleQuestionPromptChange(partIndex, qIndex, e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    className={classes.editField}
                />
                <TextField
                    label="Keywords"
                    value={question.keywords || ""}
                    onChange={(e) => handleQuestionKeywordsChange(partIndex, qIndex, e.target.value)}
                    fullWidth
                    size="small"
                    className={classes.editField}
                />
                <TextField
                    label="Help question"
                    value={question.hint1 || ""}
                    onChange={(e) => handleQuestionHint1Change(partIndex, qIndex, e.target.value)}
                    fullWidth
                    size="small"
                    className={classes.editField}
                />
            </Box>
        );
    };

    const renderDecisionPointEditable = (partIndex: number, dpIndex: number) => {
        const part = parsedStory?.parts[partIndex];
        if (!part?.decisionPoints) return null;
        const dp = part.decisionPoints[dpIndex];

        return (
            <Box key={dpIndex} className={classes.decisionPointEditBlock}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Decision Point {dpIndex + 1}
                </Typography>
                <TextField
                    label="Decision Text"
                    value={dp.decisionText}
                    onChange={(e) => handleDecisionTextChange(partIndex, dpIndex, e.target.value)}
                    fullWidth
                    multiline
                    size="small"
                    className={classes.editField}
                />
                {dp.branches.map((branch, bIndex) => (
                    <Box key={bIndex} className={classes.branchEditRow}>
                        <Chip label={branch.branchLabel} size="small" variant="outlined" />
                        <TextField
                            label="Choice Text"
                            value={branch.choiceText}
                            onChange={(e) => handleBranchChoiceTextChange(partIndex, dpIndex, bIndex, e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Target Part</InputLabel>
                            <Select
                                value={branch.targetPartNumber}
                                label="Target Part"
                                onChange={(e) =>
                                    handleBranchTargetChange(partIndex, dpIndex, bIndex, Number(e.target.value))
                                }
                            >
                                {parsedStory!.parts.map((p) => (
                                    <MenuItem key={p.partNumber} value={p.partNumber}>
                                        Part {p.partNumber}
                                        {p.partLabel ? ` - ${p.partLabel}` : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderStoryParts = () => {
        if (!parsedStory?.parts || parsedStory.parts.length === 0) {
            return (
                <Alert severity="warning">No story parts found. Please go back and upload a valid story file.</Alert>
            );
        }

        return (
            <div>
                {parsedStory.parts.map((part, index) => {
                    const isEditing = editingParts[index] === true;
                    const conf = part.parseConfidence ?? 1.0;
                    const defaultExpanded = index === 0 || conf < 0.5;

                    return (
                        <Accordion key={index} defaultExpanded={defaultExpanded}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <div className={classes.accordionSummaryContent}>
                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                        Part {part.partNumber}: {part.partLabel || `Section ${part.partNumber}`}
                                    </Typography>
                                    {renderConfidenceChip(part)}
                                    <Box sx={{ ml: "auto" }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleEditMode(index);
                                            }}
                                            title={isEditing ? "Done editing" : "Edit section"}
                                        >
                                            {isEditing ? <DoneIcon color="primary" /> : <EditIcon />}
                                        </IconButton>
                                    </Box>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails style={{ flexDirection: "column", gap: 16 }}>
                                {/* Part Content */}
                                <div>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ textTransform: "uppercase" }}
                                    >
                                        Content
                                    </Typography>
                                    {isEditing ? (
                                        <TextField
                                            value={part.content}
                                            onChange={(e) => handleContentChange(index, e.target.value)}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            size="small"
                                            className={classes.editField}
                                        />
                                    ) : (
                                        <Typography variant="body2" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                                            {part.content}
                                        </Typography>
                                    )}
                                </div>

                                {/* Questions */}
                                {part.questions && part.questions.length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{ textTransform: "uppercase" }}
                                        >
                                            Questions ({part.questions.length})
                                        </Typography>
                                        {isEditing ? (
                                            <div>
                                                {part.questions.map((_q, qIndex) =>
                                                    renderQuestionEditable(index, qIndex),
                                                )}
                                            </div>
                                        ) : (
                                            <List dense>
                                                {part.questions.map((question, qIndex) =>
                                                    renderQuestionReadOnly(question, qIndex),
                                                )}
                                            </List>
                                        )}
                                    </div>
                                )}

                                {/* Decision Points */}
                                {part.decisionPoints && part.decisionPoints.length > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{ textTransform: "uppercase" }}
                                        >
                                            Decision Points ({part.decisionPoints.length})
                                        </Typography>
                                        {isEditing ? (
                                            <div>
                                                {part.decisionPoints.map((_dp, dpIndex) =>
                                                    renderDecisionPointEditable(index, dpIndex),
                                                )}
                                            </div>
                                        ) : (
                                            part.decisionPoints.map((dp, dpIndex) => (
                                                <div
                                                    key={dpIndex}
                                                    style={{
                                                        marginTop: 8,
                                                        paddingLeft: 16,
                                                        borderLeft: "2px solid #ccc",
                                                    }}
                                                >
                                                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                        {dp.decisionText}
                                                    </Typography>
                                                    <List dense>
                                                        {dp.branches.map((branch, bIndex) => (
                                                            <ListItem key={bIndex}>
                                                                <ListItemText
                                                                    primary={`${branch.branchLabel}: ${branch.choiceText}`}
                                                                    secondary={`\u2192 Go to Part ${branch.targetPartNumber}`}
                                                                    primaryTypographyProps={{
                                                                        variant: "body2",
                                                                    }}
                                                                    secondaryTypographyProps={{
                                                                        variant: "caption",
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </div>
        );
    };

    const canPublish = validation?.valid || false;

    return (
        <div className={classes.container}>
            {/* Story Metadata Summary */}
            <VCard className={classes.summaryCard}>
                <Typography variant="h6" gutterBottom>
                    <AccountTreeIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
                    Story Metadata
                </Typography>
                {renderStoryInfo()}
            </VCard>

            {/* Story Parts Review */}
            <VCard className={classes.summaryCard}>
                <Typography variant="h6" gutterBottom>
                    <DescriptionIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
                    Story Parts & Structure
                </Typography>
                {renderStoryParts()}
            </VCard>

            {/* Validation Status */}
            <VCard className={classes.summaryCard}>
                <Typography variant="h6" gutterBottom>
                    <WarningIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
                    Validation Status
                </Typography>
                {renderValidationResults()}
            </VCard>

            {/* Save/Publish Actions */}
            <VCard className={classes.summaryCard}>
                <Typography variant="h6" gutterBottom>
                    <PublishIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
                    Save Story
                </Typography>

                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Review all the information above. You can save this story as a draft to edit later, or publish it
                    immediately to make it available for sessions.
                </Typography>

                {!canPublish && (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        Your story has validation errors that must be resolved before publishing. You can still save as
                        a draft and fix these issues later.
                    </Alert>
                )}

                <div className={classes.actions}>
                    <SecondaryButton onClick={handleSaveAsDraft} disabled={loading}>
                        Save as Draft
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={handlePublish}
                        disabled={loading || !canPublish}
                        startIcon={<PublishIcon />}
                    >
                        {loading ? "Processing..." : "Publish Story"}
                    </PrimaryButton>
                </div>
            </VCard>
        </div>
    );
};
